import { useCallback, useEffect, useState } from "react";
import { Dimmer, Loader } from "semantic-ui-react";
import { connect, type ConnectedProps } from "react-redux";
import PublishValidationInfo from "../../../../components/publishValidationInfo/PublishValidationInfo";
import { WizardNew as Wizard } from "../../../../components/wizard";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import Configure from "../SurveyDetails/Configure/Configure";
import Content from "features/Library/Surveys/SurveyDetails/SurveyContent/SurveyContent";
import { CreateSurveySteps, showLastModified } from "./types";
import { resetAction, tryPublish } from "../state/actions/surveyContentValidationActions";
import * as rtnEvents from "../../../Application/services/realTimeNotification/events/library/libraryEvents";
import { surveysStateSelector } from "../state/surveyReducer";
import { clearSurvey } from "../state/actions/surveyDetailsActions";
import { useWizardStepsManager } from "../../../../hooks/useWizardStepsManager";
import Section from "../../../../components/wizard/Section";
import { useRtn } from "../../../../hooks/useRtn";
import navigationUtils from "../../../../utils/navigationUtils";
import { PublishedStatusTypes, RouteNames } from "../../../../enums";
import SettingsTab from "../SurveyDetails/Settings/SettingsTab";
import { bindAction } from "../../../../interfaces";
import { useLocation, useNavigate } from "react-router";
import AddAssetsToPackModal from "components/modal/AddAssetsToPacksModal/AddAssetsToPackModal";

export type CreateSurveyPropsAll = PropsFromRedux;
const pageIndexes = [CreateSurveySteps.Configuration, CreateSurveySteps.Settings, CreateSurveySteps.Content];
const events = [rtnEvents.SurveyPublishSuccess];
export const CreateSurvey = (props: CreateSurveyPropsAll) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isValid, setIsValid] = useState(false);
  const [showAddPacks, setShowAddPacks] = useState(false);
  const [wizardPages, { onNext, onPrevious, goToPage, onFinish }, subscribeOnActiveIndexChanged] =
    useWizardStepsManager(pageIndexes);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(
    () => {
      return () => {
        props.onUnmount();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    const handler = (_: any, activeStepIndex: CreateSurveySteps) => {
      setActiveTab(activeStepIndex);
    };
    return subscribeOnActiveIndexChanged(handler);
  }, [subscribeOnActiveIndexChanged]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onPublished = useCallback(() => setShowAddPacks(true), []);

  useRtn(events, onPublished);

  const handleCancelClick = () => navigationUtils.goBackOrDefault(location, navigate, `/${RouteNames.contentSurveys}`);

  const handleOnFinish = async () => {
    onFinish?.();
    props.tryPublish(props.surveyId);
  };

  const getPublishedStatus = () => (props.isEntityCreated ? PublishedStatusTypes.notPublished : null);

  const renderCustomHeader = (changeActiveIndex: (index: number) => void) => {
    const hasActiveErrors = !props.contentResolved;
    const onErrorClick = () => {
      changeActiveIndex(CreateSurveySteps.Content);
      goToPage(CreateSurveySteps.Content);
    };
    return (
      props.contentErrorMode && (
        <PublishValidationInfo
          unableToPublish={hasActiveErrors}
          readyToPublish={!hasActiveErrors}
          onErrorButtonClick={onErrorClick}
        />
      )
    );
  };

  const onPreviousAsync = async (activeStepIndex: number, nextIndex: number) => {
    onPrevious(activeStepIndex, nextIndex);
  };

  return (
    <>
      <Dimmer active={props.isCreating} inverted>
        <Loader active={props.isCreating} />
      </Dimmer>
      <Wizard
        title="Create Survey"
        finishButtonLabel="Finish"
        onCancel={handleCancelClick}
        onProgress={onNext}
        onRegressAsync={onPreviousAsync}
        onFinish={handleOnFinish}
        publishedStatus={getPublishedStatus()}
        isSaveInProgress={props.isSaving}
        progressSavedDate={showLastModified.has(activeTab) ? props.dateModified : undefined}
        isFinishButtonDisabled={!props.contentResolved}
        renderCustomHeader={renderCustomHeader}
      >
        <Section label="Configuration" className="scrollable-content" isLocked={!isValid} required>
          <Configure
            acceptHandlers={wizardPages[CreateSurveySteps.Configuration]}
            onIsValidChange={setIsValid}
            mode={"create"}
          />
        </Section>
        <Section label="Settings" className="scrollable-content">
          <SettingsTab acceptHandlers={wizardPages[CreateSurveySteps.Settings]} />
        </Section>
        <Section label="Content" className="scrollable-content">
          <Content acceptHandlers={wizardPages[CreateSurveySteps.Content]} />
        </Section>
      </Wizard>
      <AddAssetsToPackModal
        showModal={showAddPacks}
        onClose={/* istanbul ignore next */ () => navigate(`/${RouteNames.contentSurveys}`)}
        onComplete={() => {}}
        selectedItemIds={[props.surveyId]}
        contentType="Survey"
      />
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const surveys = surveysStateSelector(state);
  return {
    surveyId: surveys.base.surveysEntityStateReducer.entityId,
    isEntityCreated: surveys.base.surveysEntityStateReducer.entityId > 0,
    isSaving: surveys.base.surveysEntityStateReducer.isEntityCommandInProgress,
    isCreating: surveys.base.surveysEntityStateReducer.changingEntityState,
    dateModified: surveys.base.surveysEntityStateReducer.lastModifiedDateTime,
    contentErrorMode: surveys.base.surveysContentValidationReducer.errorMode,
    contentFormIsValid: surveys.base.surveysContentValidationReducer.isFormValid,
    contentResolved: surveys.base.surveysContentValidationReducer.resolved,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  tryPublish: bindAction(tryPublish, dispatch),
  onUnmount: () => {
    dispatch(resetAction());
    dispatch(clearSurvey());
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(CreateSurvey);
export default ConnectedComponent;
